export type ApiErrorCode =
  | 'CONTEXT_MISMATCH'
  | 'BRANCH_SCOPE_FORBIDDEN'
  | 'NO_ACTIVE_ORGANIZATION'
  | 'INSUFFICIENT_ORG_ROLE'
  | 'LEGACY_CONTEXT_DISABLED'
  | 'NOT_AN_EMPLOYEE';

/**
 * The code `SelfEmployeeGuard` throws when the caller has no employment row at the
 * restaurant in scope.
 */
export const NOT_AN_EMPLOYEE: ApiErrorCode = 'NOT_AN_EMPLOYEE';

/**
 * 403 codes that a fresh tenant context can actually resolve. Three are deliberately NOT
 * here, because a cookie rewrite cannot fix any of them and recovery would only loop:
 *
 * - INSUFFICIENT_ORG_ROLE — reports the caller's organization role against what the
 *   endpoint requires. Treating it as recoverable made every member 403 clear the
 *   cookie and reload, and the reload reset the in-memory lock meant to stop exactly
 *   that — an endless loop that never reached the dashboard.
 * - LEGACY_CONTEXT_DISABLED — the portal called a retired route. A reload re-runs the
 *   same code, so recovery would loop instead of fixing anything.
 * - NOT_AN_EMPLOYEE — an answer, not a failure: the caller was never hired at this
 *   restaurant. `useSelfEmployee` renders it as an empty state.
 */
const CONTEXT_CODES: ApiErrorCode[] = [
  'CONTEXT_MISMATCH',
  'BRANCH_SCOPE_FORBIDDEN',
  'NO_ACTIVE_ORGANIZATION',
];

export function readErrorCode(error: unknown): string | null {
  const data = (error as { response?: { data?: unknown } })?.response?.data as
    | { code?: string; message?: unknown }
    | undefined;
  if (!data) return null;
  if (typeof data.code === 'string') return data.code;
  if (data.message && typeof data.message === 'object' && 'code' in data.message) {
    const nested = (data.message as { code?: unknown }).code;
    return typeof nested === 'string' ? nested : null;
  }
  return null;
}

export function isContextMismatch(error: unknown): boolean {
  const status = (error as { response?: { status?: number } })?.response?.status;
  if (status !== 403) return false;
  const code = readErrorCode(error);
  return code !== null && CONTEXT_CODES.includes(code as ApiErrorCode);
}

export function isAuthExpired(error: unknown): boolean {
  return (error as { response?: { status?: number } })?.response?.status === 401;
}
